import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone, Share, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showIOSSteps, setShowIOSSteps] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a delay if not dismissed recently
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (!dismissedTime || (Date.now() - parseInt(dismissedTime)) > sevenDays) {
        setTimeout(() => setShowPrompt(true), 4000);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Show iOS prompt after delay
    if (iOS && !standalone) {
      const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (!dismissedTime || (Date.now() - parseInt(dismissedTime)) > sevenDays) {
        setTimeout(() => setShowPrompt(true), 4000);
      }
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  // Don't show if already installed
  if (isStandalone) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-20 left-3 right-3 sm:left-auto sm:right-4 sm:w-[340px] z-50"
        >
          <div className="bg-zinc-900 border border-teal-500/30 rounded-2xl shadow-[0_0_40px_rgba(20,184,166,0.15)] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500/10 to-cyan-500/10 px-4 py-3 flex items-center justify-between border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Add to Home Screen</h3>
                  <p className="text-teal-400 text-xs">Quick access anytime!</p>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="text-zinc-500 hover:text-white transition-colors p-1.5 hover:bg-white/5 rounded-lg"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {isIOS ? (
                // iOS Instructions - More detailed and friendly
                <div className="space-y-3">
                  {!showIOSSteps ? (
                    <>
                      <p className="text-zinc-300 text-sm">
                        Add TripleTC to your home screen for the best experience - works like a real app!
                      </p>
                      <Button 
                        onClick={() => setShowIOSSteps(true)}
                        className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400"
                      >
                        Show Me How
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-white font-medium text-sm">3 Easy Steps:</p>
                      
                      {/* Step 1 */}
                      <div className="flex items-start gap-3 bg-zinc-800/50 rounded-xl p-3">
                        <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          1
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium mb-1">Tap the Share button</p>
                          <div className="flex items-center gap-2 text-zinc-400 text-xs">
                            <span>Look for</span>
                            <div className="bg-blue-500 p-1 rounded">
                              <Share className="w-3 h-3 text-white" />
                            </div>
                            <span>at the bottom of Safari</span>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div className="flex items-start gap-3 bg-zinc-800/50 rounded-xl p-3">
                        <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          2
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium mb-1">Scroll down & tap "Add to Home Screen"</p>
                          <div className="flex items-center gap-2 text-zinc-400 text-xs">
                            <span>Look for</span>
                            <div className="bg-zinc-700 p-1 rounded border border-zinc-600">
                              <Plus className="w-3 h-3 text-white" />
                            </div>
                            <span>in the menu</span>
                          </div>
                        </div>
                      </div>

                      {/* Step 3 */}
                      <div className="flex items-start gap-3 bg-zinc-800/50 rounded-xl p-3">
                        <div className="w-7 h-7 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          3
                        </div>
                        <div className="flex-1">
                          <p className="text-white text-sm font-medium">Tap "Add" in the top right</p>
                          <p className="text-zinc-400 text-xs">That's it! TripleTC will appear on your home screen</p>
                        </div>
                      </div>

                      <Button
                        onClick={handleDismiss}
                        variant="outline"
                        className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                      >
                        Got it, thanks!
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                // Android/Chrome - One-click install
                <div className="space-y-3">
                  <p className="text-zinc-300 text-sm">
                    Install TripleTC for quick access from your home screen - works offline too!
                  </p>
                  
                  {deferredPrompt ? (
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleInstall}
                        className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Install Now
                      </Button>
                      <Button
                        onClick={handleDismiss}
                        variant="outline"
                        className="border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                      >
                        Later
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleDismiss}
                      variant="outline"
                      className="w-full border-zinc-700 text-zinc-300"
                    >
                      Maybe later
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default InstallPrompt;
